<!--
  PendingReviewTable.vue
  검수요청 상태 후기 전용 테이블 컴포넌트
-->
<template>
  <div class="container-wrapper">
    <div class="pending-review-container">
      <div class="pending-header">
        <h4 class="pending-title">
          <i class="fa-solid fa-clock"></i>
          검수요청
          <span class="pending-count">({{ paginationData.totalCount }}건)</span>
          <span class="pending-count">검색 조건과 관계없이 검수가 필요한 후기만 보여드립니다.</span>
        </h4>
        <div class="pending-actions">
          <a-button
            @click="bulkApprove"
            type="primary"
            class="yellow-button"
            size="small"
            v-if="selectedRowKeys.length > 0"
          >
            <i class="fa-solid fa-eye"/>
            일괄 노출 ({{ selectedRowKeys.length }})
          </a-button>
          <a-button
            @click="bulkHide"
            type="primary"
            danger
            size="small"
            v-if="selectedRowKeys.length > 0"
          >
            <i class="fa-solid fa-eye-slash"/>
            일괄 비노출 ({{ selectedRowKeys.length }})
          </a-button>
          <!-- <a-button
            @click="bulkDelete"
            type="primary"
            danger
            size="small"
            v-if="selectedRowKeys.length > 0"
          >
            <i class="fa-solid fa-trash"/>
            일괄 삭제 ({{ selectedRowKeys.length }})
          </a-button> -->
        </div>
      </div>
      
      <a-table 
        class="ant-table-striped"
        size="small"
        :columns="columns"
        :data-source="pendingList"
        :pagination="{
          current: paginationData.currentPage,
          total: paginationData.totalCount,
          pageSize: rowsPerPage,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
          pageSizeOptions: ['10', '20', '50'],
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange
        }"
        :row-selection="rowSelection"
        :loading="loading"
        row-key="_id"
        :resizable="false"
        table-layout="fixed"
      >
        <template #emptyText>
          <div class="noResult">검수요청 후기가 없습니다.</div>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import { getAccessToken } from '@/utils/tokenStore';
import { ref, reactive, computed, onMounted, nextTick, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog, convertDate, convertToLocalTime, phoneNumberFormatter } from '@/utils/utils';
import { jwtDecoder } from '@/utils/utils';

export default {
  name: 'PendingReviewTable',
  emits: ['status-changed', 'delete'],
  setup(props, { emit }) {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiInstance = null;
    
    if (instance && instance.proxy) {
      apiInstance = instance.proxy.$apiInstance;
    }

    // 반응형 상태
    const loading = ref(false);
    const pendingList = ref([]);
    const selectedRowKeys = ref([]);
    
    // 페이지네이션 상태
    const rowsPerPage = ref(10);
    const paginationData = reactive({
      currentPage: 1,
      totalCount: 0,
      totalPages: 0
    });

    // 테이블 컬럼 정의 (간소화된 버전)
    const columns = [
      {
        title: '번호',
        dataIndex: 'index',
        key: 'index',
        width: 60,
        align: 'center'
      },
      {
        title: '고객명',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 120,
        align: 'center'
      },
      {
        title: '전화번호',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 120,
        align: 'center',
        customRender: ({ record }) => {
          return phoneNumberFormatter(record.phoneNumber);
        }
      },
      {
        title: '병원명',
        dataIndex: 'companyName',
        key: 'companyName',
        width: 150,
        align: 'center'
      },
      {
        title: '수술일자',
        dataIndex: 'surgeryAt',
        key: 'surgeryAt',
        width: 100,
        align: 'center',
        customRender: ({ record }) => {
          return convertDate(record.surgeryAt);
        }
      },
      {
        title: '수술명',
        dataIndex: 'categoryName',
        key: 'categoryName',
        width: 120,
        align: 'center'
      },
      {
        title: '후기 내용',
        dataIndex: 'content',
        key: 'content',
        width: 300,
        align: 'left',
        customRender: ({ record }) => {
          const content = record.content || '';
          const displayContent = content.length > 200 ? content.substring(0, 200) + '...' : content;
          return (
            <div class="review-content-wrap">
              {displayContent}
            </div>
          );
        }
      },
      {
        title: '만족도',
        dataIndex: 'score',
        key: 'score',
        width: 80,
        align: 'center'
      },
      {
        title: '키워드',
        dataIndex: 'satisfactionPoints',
        key: 'satisfactionPoints',
        width: 200,
        align: 'center',
        customRender: ({ record }) => {
          const keywords = record.satisfactionPoints;
          if (!keywords || keywords.length === 0) {
            return '-';
          }
          
          // 키워드가 배열인 경우
          if (Array.isArray(keywords)) {
            return (
              <div class="keywords-container">
                {keywords.map((keyword, index) => (
                  <span key={index} class="keyword-tag">
                    {keyword}
                  </span>
                ))}
              </div>
            );
          }
          return keywords;
        }
      },
      {
        title: '등록일시',
        dataIndex: 'createAt',
        key: 'createAt',
        width: 150,
        align: 'center',
        customRender: ({ record }) => {
          return convertToLocalTime(record.createAt);
        }
      },
      {
        title: '작업',
        key: 'action',
        width: 220,
        align: 'center',
        fixed: 'right',
        customRender: ({ record }) => {
          return (
            <div style="display: flex; gap: 4px; justify-content: center;">
              <a-button 
                size="small"
                onClick={() => handleViewDetail(record)}
              >
                상세
              </a-button>
              <a-button 
                size="small" 
                type="primary" 
                class="yellow-button"
                onClick={() => handleStatusChange(record._id, 'exposed')}
              >
                노출
              </a-button>
              <a-button 
                size="small" 
                danger
                onClick={() => handleStatusChange(record._id, 'hidden')}
              >
                비노출
              </a-button>
              {/* <a-button 
                size="small" 
                danger
                onClick={() => handleDelete(record._id)}
              >
                삭제
              </a-button> */}
            </div>
          );
        }
      }
    ];

    // 행 선택 설정
    const rowSelection = computed(() => ({
      selectedRowKeys: selectedRowKeys.value,
      onChange: (selectedKeys) => {
        selectedRowKeys.value = selectedKeys;
      }
    }));

    // 검수요청 후기 로드
    const loadPendingReviews = async () => {
      if (!apiInstance) {
        console.error('API instance not available');
        return;
      }
      
      loading.value = true;
      
      try {
        const manufacturerId = jwtDecoder(getAccessToken());
          const params = {
          manufacturerId: manufacturerId,
          page: paginationData.currentPage,
          limit: rowsPerPage.value,
          reviewStatus: 'review_requested',
          checkValidCategoryName: 'true' // 수술명 있는 검수요청만 — 상단 통계 카드와 정합
        };
        
        const response = await apiInstance.get('/reviews', { params });
        
        if (response.data) {
          pendingList.value = response.data.data || [];
          paginationData.totalCount = response.data.pagination?.total || 0;
          paginationData.totalPages = response.data.pagination?.totalPages || 0;
          
          // 번호 설정
          pendingList.value.forEach((item, index) => {
            item.index = paginationData.totalCount - index - (paginationData.currentPage - 1) * rowsPerPage.value;
          });
        }
      } catch (error) {
        console.error('검수요청 후기 로드 실패:', error);
        pendingList.value = [];
        paginationData.totalCount = 0;
        paginationData.totalPages = 0;
      } finally {
        loading.value = false;
      }
    };

    // 상태 변경
    const handleStatusChange = async (id, reviewStatus) => {
      const statusText = {
        'exposed': '노출',
        'hidden': '비노출'
      };
      
      try {
        await apiInstance.patch(`/reviews/${id}/status`, { reviewStatus });
        message.success(`후기가 성공적으로 ${statusText[reviewStatus]}로 변경되었습니다.`);
        
        // 목록에서 해당 항목 제거
        pendingList.value = pendingList.value.filter(item => item._id !== id);
        
        // 선택된 항목에서도 제거
        selectedRowKeys.value = selectedRowKeys.value.filter(key => key !== id);
        
        // 부모 컴포넌트에 상태 변경 알림
        emit('status-changed');
      } catch (error) {
        message.error('후기 상태 변경 중 오류가 발생했습니다.');
      }
    };

    // 일괄 승인
    const bulkApprove = async () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('승인할 후기를 선택해주세요.');
        return;
      }
      
      const isConfirmed = await twoBtnDialog(
        '일괄 노출',
        `선택한 ${selectedRowKeys.value.length}개의 후기를 노출로 변경하시겠습니까?`,
        '노출'
      );
      
      if (!isConfirmed) return;
      
      try {
        const promises = selectedRowKeys.value.map(id => 
          apiInstance.patch(`/reviews/${id}/status`, { reviewStatus: 'exposed' })
        );
        
        await Promise.all(promises);
        message.success(`${selectedRowKeys.value.length}개의 후기가 노출로 변경되었습니다.`);
        
        // 목록에서 선택된 항목들 제거
        pendingList.value = pendingList.value.filter(item => 
          !selectedRowKeys.value.includes(item._id)
        );
        
        selectedRowKeys.value = [];
        emit('status-changed');
      } catch (error) {
        message.error('일괄 승인 중 오류가 발생했습니다.');
      }
    };

    // 일괄 비노출
    const bulkHide = async () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('비노출 처리할 후기를 선택해주세요.');
        return;
      }
      
      const isConfirmed = await twoBtnDialog(
        '일괄 비노출',
        `선택한 ${selectedRowKeys.value.length}개의 후기를 비노출로 변경하시겠습니까?`,
        '비노출'
      );
      
      if (!isConfirmed) return;
      
      try {
        const promises = selectedRowKeys.value.map(id => 
          apiInstance.patch(`/reviews/${id}/status`, { reviewStatus: 'hidden' })
        );
        
        await Promise.all(promises);
        message.success(`${selectedRowKeys.value.length}개의 후기가 비노출로 변경되었습니다.`);
        
        // 목록에서 선택된 항목들 제거
        pendingList.value = pendingList.value.filter(item => 
          !selectedRowKeys.value.includes(item._id)
        );
        
        selectedRowKeys.value = [];
        emit('status-changed');
      } catch (error) {
        message.error('일괄 비노출 중 오류가 발생했습니다.');
      }
    };

    // 상세 보기
    const handleViewDetail = (record) => {
      emit('view-detail', record);
    };

    // 단건 삭제
    const handleDelete = async (id) => {
      const isConfirmed = await twoBtnDialog(
        '후기 삭제',
        '정말로 이 후기를 삭제하시겠습니까?',
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        await apiInstance.delete(`/reviews/${id}`);
        message.success('후기가 성공적으로 삭제되었습니다.');
        
        // 목록에서 해당 항목 제거
        pendingList.value = pendingList.value.filter(item => item._id !== id);
        
        // 선택된 항목에서도 제거
        selectedRowKeys.value = selectedRowKeys.value.filter(key => key !== id);
        
        // 총 개수 업데이트
        paginationData.totalCount = Math.max(0, paginationData.totalCount - 1);
        
        // 부모 컴포넌트에 삭제 알림
        emit('status-changed');
      } catch (error) {
        message.error('후기 삭제 중 오류가 발생했습니다.');
      }
    };

    // 일괄 삭제
    const bulkDelete = async () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('삭제할 후기를 선택해주세요.');
        return;
      }
      
      const isConfirmed = await twoBtnDialog(
        '일괄 삭제',
        `선택한 ${selectedRowKeys.value.length}개의 후기를 삭제하시겠습니까?`,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        const promises = selectedRowKeys.value.map(id => 
          apiInstance.delete(`/reviews/${id}`)
        );
        
        await Promise.all(promises);
        message.success(`${selectedRowKeys.value.length}개의 후기가 삭제되었습니다.`);
        
        // 삭제된 개수 저장
        const deletedCount = selectedRowKeys.value.length;
        
        // 목록에서 선택된 항목들 제거
        pendingList.value = pendingList.value.filter(item => 
          !selectedRowKeys.value.includes(item._id)
        );
        
        // 총 개수 업데이트
        paginationData.totalCount = Math.max(0, paginationData.totalCount - deletedCount);
        
        selectedRowKeys.value = [];
        emit('status-changed');
      } catch (error) {
        message.error('일괄 삭제 중 오류가 발생했습니다.');
      }
    };

    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
      paginationData.currentPage = page;
      selectedRowKeys.value = []; // 페이지 변경 시 선택 초기화
      
      // DOM 업데이트를 다음 틱으로 지연
      nextTick(() => {
        loadPendingReviews();
      });
    };

    // 페이지 크기 변경 핸들러
    const handlePageSizeChange = (current, size) => {
      paginationData.currentPage = 1;
      rowsPerPage.value = size;
      selectedRowKeys.value = []; // 페이지 크기 변경 시 선택 초기화
      
      // DOM 업데이트를 다음 틱으로 지연
      nextTick(() => {
        loadPendingReviews();
      });
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(() => {
      loadPendingReviews();
    });

    return {
      loading,
      pendingList,
      selectedRowKeys,
      rowsPerPage,
      paginationData,
      columns,
      rowSelection,
      loadPendingReviews,
      handleViewDetail,
      handleStatusChange,
      bulkApprove,
      bulkHide,
      handleDelete,
      bulkDelete,
      handlePageChange,
      handlePageSizeChange
    };
  }
};
</script>

<style scoped lang="scss">
.container-wrapper {
  margin-bottom: 20px;
}

.pending-review-container {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
}

.pending-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.pending-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
  
  i {
    color: #fa8c16;
  }
}

.pending-count {
  font-size: 14px;
  color: #666;
  font-weight: 400;
}

.pending-actions {
  display: flex;
  gap: 8px;
}

.empty-pending {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  
  p {
    margin: 8px 0 0 0;
    font-size: 14px;
  }
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 후기 내용 스타일
:deep(.review-content-wrap) {
  min-width: 280px;
  word-wrap: break-word;
  word-break: break-all;
  white-space: pre-wrap;
  line-height: 1.4;
  text-align: left;
  padding: 4px;
}

// 키워드 스타일
:deep(.keywords-container) {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
  align-items: center;
  max-width: 160px;
  margin: 0 auto;
}

:deep(.keyword-tag) {
  display: inline-block;
  padding: 2px 8px;
  background-color: #f0f8ff;
  color: #1890ff;
  border: 1px solid #d6e4ff;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1.2;
  
  &:hover {
    background-color: #e6f7ff;
    border-color: #91d5ff;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .pending-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .pending-actions {
    width: 100%;
    
    .ant-btn {
      flex: 1;
    }
  }
}
</style> 